/**
 * Короткое уведомление внизу экрана, исчезающее само («тост», snackbar), — вместо системного
 * окна с кнопкой OK там, где сообщать надо об успехе: «Скопировано», «Переслано», «Заявка
 * отправлена». Окно с OK на каждое такое действие — лишний тап и лишний взгляд: копирование
 * текста стоило три нажатия вместо двух (аудит удобства 2026-10-04).
 *
 * <p><b>«Отменить».</b> Обратимое действие (удаление, отклонение заявки, отключение звука)
 * подтверждается не вопросом «вы уверены?» до, а кнопкой «Отменить» после — так делают
 * Telegram, WhatsApp и Gmail: вопрос задерживает каждое действие, отмена — только ошибочное.
 *
 * <p><b>Точки вывода.</b> На телефоне обычный `View` рисуется под окнами `Modal`, а нижние
 * листы — это `Modal`. Поэтому выводов несколько: один в корне приложения и по одному внутри
 * каждого открытого листа. Показывает последний смонтированный — то есть верхний на экране.
 *
 * <p>Ошибки сюда не идут: ошибку человек должен заметить и прочитать, она остаётся окном
 * ({@code notifyAction}).
 */

export type ToastRequest = {
  id: number;
  message: string;
  /** Подпись кнопки справа, обычно «Отменить». Без неё кнопки нет. */
  actionLabel?: string;
  onAction?: () => void;
  /** Сколько висит. По умолчанию 3 с, с кнопкой — 5 с: на «Отменить» нужно время. */
  durationMs?: number;
};

type Outlet = (toast: ToastRequest | null) => void;

const outlets: Outlet[] = [];
let current: ToastRequest | null = null;
let nextId = 1;

function deliver(): void {
  // Уведомление видит только верхний вывод; остальные — пустые, иначе оно показалось бы
  // дважды: под листом и в нём.
  outlets.forEach((o, i) => o(i === outlets.length - 1 ? current : null));
}

/** Показать уведомление. Новое заменяет старое: очередь из «Скопировано» никому не нужна. */
export function showToast(request: Omit<ToastRequest, 'id'>): number {
  current = { ...request, id: nextId++ };
  deliver();
  return current.id;
}

/** Убрать уведомление, если оно всё ещё то же самое (таймер прежнего не гасит новое). */
export function dismissToast(id: number): void {
  if (current?.id !== id) return;
  current = null;
  deliver();
}

/** Подписка вывода; возвращает отписку. Последний подписавшийся — верхний. */
export function subscribeToastOutlet(outlet: Outlet): () => void {
  outlets.push(outlet);
  deliver();
  return () => {
    const i = outlets.lastIndexOf(outlet);
    if (i >= 0) outlets.splice(i, 1);
    outlet(null);
    deliver();
  };
}

/** Только для тестов. */
export function resetToastsForTests(): void {
  outlets.length = 0;
  current = null;
  nextId = 1;
}
